import { useCallback } from 'react';
import { FieldValueChangedErrorType, FieldValueChangedInputDataType, FieldValueChangedTriggerType } from '../types';
import { findAllFieldTypesByWorkItemField } from '../../utils/find-all-fields-types-by-wi-field';
import { getFields } from 'src/azure/services/work-items';
import { stringRequired } from '../../utils/validations';

export const initialFieldValueChange: FieldValueChangedTriggerType = {
	fieldOptions: [],
	selectedOptions: '',
	loading: true,
	fieldName: '',
	fieldNameQuery: ''
};

export default function useFieldValueChanged() {
	const getValueOptions = useCallback(async (input: {
    projectId: string;
  }) => {
		const fields = await getFields(input.projectId);
		if (fields) {
			const foundFields = fields.filter(field => field.referenceName !== 'System.TeamProject');
			return findAllFieldTypesByWorkItemField(foundFields)
				.map(item => ({ label: item.name, value: item.referenceName }));
		}

		return [];
	}, []);

	const validate = useCallback((values: {
		fieldName: string;
	}): {
		hasError(): boolean;
		errors: FieldValueChangedErrorType;
	} => {
		let hasErrors = false;
		const validations = {
			fieldName: [
				stringRequired(values.fieldName, 'This field is required')
			],
		};

		const getError = (validation: Array<string | undefined>) => {
			const foundError = validation.find(item => item !== undefined) || null;
			if (foundError) {
				hasErrors = true;
			}
			
			return foundError || null;
		};

		return {
			hasError: () => hasErrors,
			errors: {
				fieldName: getError(validations.fieldName),
			}
		};
	}, []);

	const getValues = useCallback((data: FieldValueChangedTriggerType) => {
		return {
			field: data.selectedOptions
		};
	}, []);

	const loadValues = useCallback(async (input: {
		projectId: string;
		data: FieldValueChangedInputDataType
	}): Promise<FieldValueChangedTriggerType> => {
		const options = await getValueOptions({projectId: input.projectId});

		return {
			fieldName: options.find(item => item.value === input.data.field)?.label || '',
			fieldOptions: options,
			loading: false,
			selectedOptions: input.data.field,
			fieldNameQuery: options.find(item => item.value === input.data.field)?.label || ''
		};
	}, []);

	return {
		getValueOptions,
		validate,
		getValues,
		loadValues
	};
}